/**
 * Approve / Approve-with-adjustment / Reject-with-note action cluster.
 *
 * Spec §9.7: per-row affordances on the Governance Approver Inbox. This
 * component renders ONLY the button cluster — the expanded-form regions
 * (NTE adjustment input, reject note textarea) live in `InboxRow.tsx`
 * because they're part of the row card layout, not the action cluster.
 *
 * State model: only one row across the whole inbox can be expanded at a
 * time, so the parent index.tsx owns the expansion state. This component
 * receives the row's current expansion mode (or null) and emits onExpand
 * to request a mode switch, plus onApprove for the one-click direct path.
 */
import Button from '@/componentsv2/Button';

export type ExpandedMode = 'approve_adjustment' | 'reject_note';

interface ApproveActionsProps {
  /** When this row is the expanded one, which mode it's in. Null if not expanded. */
  expandedMode: ExpandedMode | null;
  /** Direct one-click approve. */
  onApprove: () => void;
  /** Request expansion into a mode (or collapse if mode === null). */
  onExpand: (mode: ExpandedMode | null) => void;
  /** A decision is in flight; none of the three may start another. */
  busy?: boolean;
  /** The decision in flight is this one-click approve, so it carries the progress cue. */
  approving?: boolean;
  /** False when no adjustment can be proposed on this engagement; the host says why. */
  canAdjust?: boolean;
}

/**
 * `min-h-48` on all three: they are irreversible financial decisions, and
 * `size="small"` alone is 32px (the tertiary one about 20px, since it has no
 * vertical padding). 48 rather than 44 because 44 is absent from this config's
 * spacing scale.
 */
const TOUCH_TARGET = 'min-h-48';

export default function ApproveActions({
  expandedMode,
  onApprove,
  onExpand,
  busy = false,
  approving = false,
  canAdjust = true,
}: ApproveActionsProps): JSX.Element {
  const isAdjustment = expandedMode === 'approve_adjustment';
  const isReject = expandedMode === 'reject_note';

  return (
    <div className="flex flex-wrap items-center gap-8">
      <Button
        variant="primary"
        size="small"
        className={TOUCH_TARGET}
        onClick={onApprove}
        disabled={busy}
        loading={approving}
      >
        Approve
      </Button>
      <Button
        variant="secondary"
        size="small"
        className={TOUCH_TARGET}
        onClick={() => onExpand(isAdjustment ? null : 'approve_adjustment')}
        aria-pressed={isAdjustment}
        disabled={busy || !canAdjust}
      >
        Approve with adjustment
      </Button>
      <Button
        variant="tertiary"
        size="small"
        className={TOUCH_TARGET}
        onClick={() => onExpand(isReject ? null : 'reject_note')}
        aria-pressed={isReject}
        disabled={busy}
      >
        Reject with note
      </Button>
    </div>
  );
}
